import { inject, NgModule } from '@angular/core';
import { mapToCanActivate, RouterModule, Routes } from '@angular/router';
import { SignedOutComponent } from '../authorization/signed-out/signed-out.component';
import { AccessDeniedComponent } from '../core/access-denied/access-denied.component';
import { ServiceUnavailableComponent } from '../core/service-unavailable/service-unavailable.component';
import { FooterComponent } from '../footer/footer/footer.component';
import { HeaderComponent } from '../header/header/header.component';
import { EnterpriseConfigurationResolver } from '../shared/modules/enterprise-configuration/enterprise-configuration-resolver';
import { EulaGuard } from './eula.guard';
import { IsAuthenticatedGuard } from './is-authenticated.guard';
import { RedirectGuard } from './redirect-guard';

const routes: Routes = [
  // If no path at all, go to home.
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  {
    // Check all paths for old /#!/ path and if matched, redirect to UME
    path: '',
    canActivate: [RedirectGuard],
    children: [
      {
        // Private routes
        path: '',
        canActivate: mapToCanActivate([IsAuthenticatedGuard, EulaGuard]),
        children: [
          {
            path: '',
            data: { header: true, footer: true },
            children: [
              { path: '', component: HeaderComponent, outlet: 'header' },
              { path: '', component: FooterComponent, outlet: 'footer' },
              // Authenticated Routes that display a header and footer go here.
              {
                path: 'demo',
                resolve: {
                  enterpriseConfiguration: () => inject(EnterpriseConfigurationResolver).resolve(),
                },
                loadChildren: () => import('../demo/demo.module').then((m) => m.DemoModule),
              },
              {
                path: 'search',
                // To support AOT compilation, do not use the function syntax like:
                // loadChildren: () => import('./search/search.module').then(module => module.SearchModule)
                // see https://github.com/angular/angular/issues/23878#issuecomment-423163527
                // Something changes in Angular 8 but not clear if it's documentation or a bug fix.
                // module class name appears after the hash
                loadChildren: () => import('../search/search.module').then((m) => m.SearchModule),
              },
              {
                path: 'uam',
                loadChildren: () =>
                  import('../user-management/user-management.module').then((m) => m.UserManagementModule),
              },
              {
                path: 'user',
                loadChildren: () => import('../user/user.module').then((m) => m.UserModule),
              },
              {
                path: 'products',
                loadChildren: () => import('../products/products.module').then((m) => m.ProductsModule),
              },
              {
                path: 'data',
                loadChildren: () => import('../products/products.module').then((m) => m.ProductsModule),
              },
              {
                path: 'software',
                loadChildren: () => import('../software/software.module').then((m) => m.SoftwareModule),
              },
              {
                path: 'autodownload',
                loadChildren: () =>
                  import('../automatic-download/automatic-download.module').then((m) => m.AutomaticDownloadModule),
              },
              { path: 'samples', loadChildren: () => import('../samples/samples.module').then((m) => m.SamplesModule) },
              {
                path: 'shop',
                loadChildren: () => import('../shop/shop.module').then((m) => m.ShopModule),
              },
              {
                path: 'usecases',
                loadChildren: () => import('../usecases/usecases.module').then((m) => m.UsecasesModule),
              },
              // {
              //   loadChildren: () => import('../clients/clients.module').then(m => m.ClientsModule)
              // }
            ],
          },
        ],
      },
      {
        // Public Routes
        path: '',
        children: [
          {
            // Header and Footer routes
            path: '',
            children: [
              {
                path: '',
                data: { header: true, footer: true },
                children: [
                  { path: '', component: HeaderComponent, outlet: 'header' },
                  { path: '', component: FooterComponent, outlet: 'footer' },
                  {
                    path: 'home',
                    canActivate: mapToCanActivate([EulaGuard]),
                    loadChildren: () => {
                      return import('../home/home.module').then((m) => {
                        return m.HomeModule;
                      });
                    },
                  },
                  {
                    path: 'help',
                    children: [
                      {
                        path: 'contact-sales',
                        loadChildren: () =>
                          import('../contact-sales/contact-sales.module').then((m) => m.ContactSalesModule),
                      },
                    ],
                  },
                  {
                    path: 'visualization',
                    children: [
                      {
                        path: 'map-explorer',
                        loadChildren: () =>
                          import('../map-explorer/map-explorer.module').then((m) => m.MapExplorerModule),
                      },
                    ],
                  },
                  {
                    path: 'license-agreement',
                    loadChildren: () =>
                      import('../license-agreement/license-agreement.module').then((m) => m.LicenseAgreementModule),
                  },
                  {
                    path: 'signed-out',
                    component: SignedOutComponent,
                  },
                  {
                    path: 'service-unavailable',
                    component: ServiceUnavailableComponent, // should be called when salesforce api is down and server returns 503
                  },
                ],
              },
              {
                path: '',
                data: { header: false, footer: false },
                children: [
                  {
                    path: 'generating-pdf',
                    loadChildren: () => import('../pdf-window/pdf-window.module').then((m) => m.PdfWindowModule),
                  },
                ],
              },
            ],
          },
          {
            // No Header and Footer Included routes
            path: '',
            data: { header: false, footer: true },
            children: [
              {
                path: '',
                component: FooterComponent,
                outlet: 'footer',
                data: {
                  mode: 'signOn',
                },
              },
              // {
              //   path: 'sign-in',
              //   component: LoginComponent
              // },
              // {
              //   path: 'forgot-password',
              //   component: ForgotPasswordComponent
              // },
              // {
              //   path: 'reset-password',
              //   component: ResetPasswordComponent // Should be called with recoveryToken query string parameter
              // },
              // {
              //   path: 'implicit/callback',
              //   component: OktaCallbackComponent
              // },
              // {
              //   path: 'claim-account',
              //   component: ClaimAccountComponent // should be called with recoveryToken query string parameter
              // },
              {
                path: 'access-denied',
                component: AccessDeniedComponent, // should be called with recoveryToken query string parameter
              },
            ],
          },
        ],
      },
    ],
  },
  // Default route
  // Route not found path
  { path: '**', redirectTo: '/home' },
];

@NgModule({
  // initial navigation is set to false so that route guards do not fire before application initialization
  // see https://github.com/angular/angular/issues/29828
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      initialNavigation: 'disabled',
    }),
  ],
  providers: [IsAuthenticatedGuard, RedirectGuard],
  exports: [RouterModule],
})
export class AppRoutingModule {}
